import React from "react";
import PropTypes from "prop-types";

import { flowRight as compose } from 'lodash';
import withStyles from "isomorphic-style-loader/withStyles";
import cx from 'classnames';
import { injectIntl } from "react-intl";

// Components
import HomeSlider from "../../components/Home/HomeSlider";
import NewsBox from "../../components/Home/NewsBox";
import Loader from "../../components/Loader";
import HomeKindofTrip from "../../components/Home/HomeKindofTrip";
import PopularLocationGrid from "../../components/Home/PopularLocationGrid";
import Feedback from '../../components/Feedback';
import { withQuery } from "../../hoc/withGraphql";

// GraphQL
import {
  getImageBannerData, GetMostViewedListing, getPopularLocationAdmin,
  getRecommend, getStaticInfoData
} from "../../lib/graphql";

// Skeleton Loader
import Shimmer from "../../components/Skeleton/Shimmer";
import HomeSliderSkeleton from "../../components/Skeleton/HomeSliderSkeleton";
import HomePopularSkeleton from "../../components/Skeleton/HomePopularSkeleton";
import NewsBoxSkeleton from "../../components/Skeleton/NewsBoxSkeleton";
import HomeKindofTripSkeleton from "../../components/Skeleton/HomeKindofTripSkeleton";
import LayoutSections from "../../components/Home/LayoutSections";
import s from "./Home.css";
import l from '../../components/Skeleton/Skeleton.css';

const Homepage = ({
  layoutType,
  wholeData,
  getRecommendData,
  getMostViewedListingData,
  getPopularLocationData,
  getImageBannerData,
  getStaticBlockInfoData
}) => {
  let title, content, getHomeBanner;

  const feedbackSpacing = getStaticBlockInfoData?.getStaticInfo?.length
    ? !getStaticBlockInfoData?.getStaticInfo?.some(item => item?.isEnable === false)
    : false;

  if (wholeData) {
    title = wholeData?.title;
    content = wholeData?.content;
    getHomeBanner = wholeData?.getHomeBanner;
  }

  if (!wholeData) return <Loader type="text" />;

  return (
    <div className={s.root}>
      <div>
        <LayoutSections
          layoutType={layoutType}
          title={title}
          content={content}
          homeBannerImages={getHomeBanner}
          cx={cx}
          s={s}
        />

        {getPopularLocationData && getPopularLocationData.loading ? (
          <div className={cx(s.popularGridWidth)}>
            <div className={s.container}>
              <div className={cx(l.popularSkeletonTwoTitle)}>
                <Shimmer />
              </div>
              <div className='fourCardSkeleton'>
                {[1, 2, 3, 4].map(n => <HomePopularSkeleton key={n} />)}
              </div>
            </div>
          </div>
        ) : (
          getPopularLocationData?.getPopularLocationAdmin?.length > 0 &&
          <div className={s.popularGridWidthPadding}>
            <div className={cx(s.popularGridWidth)}>
              <div className={s.container}>
                <PopularLocationGrid data={getPopularLocationData.getPopularLocationAdmin} />
              </div>
            </div>
          </div>
        )}
      </div>

      {getRecommendData && getRecommendData.loading ? (
        <div className={s.homePageContainer}>
          <div className={cx(s.container, s.skeletonContainerPadding)}>
            <div className={cx(l.popularSkeletonTitle)}>
              <Shimmer />
            </div>
            <div className='fourCardSkeleton'>
              {[1, 2, 3, 4].map(n => <HomeSliderSkeleton key={n} />)}
            </div>
          </div>
        </div>
      ) : (
        getRecommendData?.getRecommend?.length > 0 &&
        <div className={s.homePageContainer}>
          <div className={s.container}>
            <HomeSlider data={getRecommendData.getRecommend} isRecommand={true} skeletonClass='fourCardSkeleton' />
          </div>
        </div>
      )}


      {getMostViewedListingData && getMostViewedListingData.loading ? (
        <div className={s.homePageContainer}>
          <div className={cx(s.container, s.skeletonContainerPadding)}>
            <div className={cx(l.popularSkeletonTitle)}>
              <Shimmer />
            </div>
            <div className='fourCardSkeleton'>
              {[1, 2, 3, 4].map(n => <HomeSliderSkeleton key={n} />)}
            </div>
          </div>
        </div>
      ) : (
        getMostViewedListingData?.GetMostViewedListing?.length > 0 &&
        <div className={s.homePageContainer}>
          <div className={s.container}>
            <HomeSlider data={getMostViewedListingData.GetMostViewedListing} isRecommand={false} skeletonClass='fourCardSkeleton' />
          </div>
        </div>
      )}

      {getImageBannerData && getImageBannerData.loading ? (
        <div className={s.homePageContainer}>
          <div className={s.container}>
            <NewsBoxSkeleton />
          </div>
        </div>
      ) : (
        <div className={s.homePageContainer}>
          <div className={s.container}>
            <NewsBox data={getImageBannerData?.getImageBanner} />
          </div>
        </div>
      )}

      <div className={cx(s.homePageContainer, (!feedbackSpacing) && s.feedbackSpacingContainer)}>
        <Feedback />
      </div>

      {getStaticBlockInfoData && getStaticBlockInfoData.loading ? (
        <div className="hidden-xs">
          <div className={s.container}>
            <HomeKindofTripSkeleton />
          </div>
        </div>
      ) : (
        <div className="hidden-xs">
          <div className={s.container}>
            <HomeKindofTrip data={getStaticBlockInfoData.getStaticInfo} />
          </div>
        </div>
      )}
    </div>
  );
};

Homepage.propTypes = {
  getRecommendData: PropTypes.shape({ loading: PropTypes.bool, getRecommend: PropTypes.array }),
  getImageBannerData: PropTypes.shape({ loading: PropTypes.bool, getImageBanner: PropTypes.object }),
  getMostViewedListingData: PropTypes.shape({ loading: PropTypes.bool, GetMostViewedListing: PropTypes.array }),
  getPopularLocationData: PropTypes.shape({ loading: PropTypes.bool, GetMostViewedListing: PropTypes.array }),
  getStaticBlockInfoData: PropTypes.shape({ loading: PropTypes.bool })
};

Homepage.defaultProps = {
  getRecommendData: { loading: true },
  getPopularLocationData: { loading: true },
  getMostViewedListingData: { loading: true },
  getImageBannerData: { loading: true },
  getStaticBlockInfoData: { loading: true }
};

export default compose(
  injectIntl,
  withStyles(s, l),
  withQuery(getRecommend, { name: "getRecommendData", options: { ssr: false } }),
  withQuery(getPopularLocationAdmin, { name: "getPopularLocationData", options: { ssr: false } }),
  withQuery(GetMostViewedListing, { name: "getMostViewedListingData", options: { ssr: false } }),
  withQuery(getImageBannerData, { name: "getImageBannerData", options: { ssr: false } }),
  withQuery(getStaticInfoData, { name: "getStaticBlockInfoData", options: { ssr: false } })
)(Homepage);